import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

const CODE_TTL_SECONDS = 10 * 60;
const RESEND_COOLDOWN_SECONDS = 60;

const VerifyEmail = () => {
  const { verifyEmail, resendVerification, verifyAdminLogin, resendAdminCode } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const isAdminMode = location.state?.mode === 'admin';
  const doVerify = isAdminMode ? verifyAdminLogin : verifyEmail;
  const doResend = isAdminMode ? resendAdminCode : resendVerification;

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(CODE_TTL_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMsg, setResendMsg] = useState('');
  const timerRef = useRef(null);
  const cooldownRef = useRef(null);

  // Sans email en state (accès direct à l'URL), impossible de vérifier quoi que ce soit
  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  // Compte à rebours visuel de l'expiration du code (10 min) — l'expiration
  // réelle est de toute façon vérifiée côté serveur, ceci est indicatif.
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Cooldown du bouton "Renvoyer le code"
  useEffect(() => {
    if (resendCooldown <= 0) return;
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [resendCooldown]);

  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) return;

    setLoading(true);
    setError('');
    const result = await doVerify(email, code.trim());

    if (result.success) {
      if (result.user && ['admin', 'manager'].includes(result.user.role)) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else {
      if (result.expired) setExpired(true);
      setError(result.error);
      setCode('');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResendMsg('');
    setError('');
    const result = await doResend(email);
    if (result.success) {
      setResendMsg(result.message || t('auth.verifyResendSuccess'));
      setExpired(false);
      setSecondsLeft(CODE_TTL_SECONDS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setCode('');
    } else {
      setError(result.error);
      if (result.retryAfterSeconds) setResendCooldown(result.retryAfterSeconds);
    }
  };

  if (!email) return null;

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>📈 CryptoBot</h1>
          <p>{isAdminMode ? t('auth.verifyAdminTitle') : t('auth.verifyTitle')}</p>
        </div>

        <p className="verify-email-hint">
          {t('auth.verifySentTo')} <strong>{email}</strong>
        </p>

        {error && <div className="auth-error">{error}</div>}
        {resendMsg && <div className="verify-resend-success">{resendMsg}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="code">{t('auth.verifyCodeLabel')}</label>
            <input
              type="text"
              id="code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="verify-code-input"
              autoComplete="one-time-code"
              autoFocus
            />
          </div>

          <div className="verify-countdown">
            {expired ? (
              <span className="verify-expired">{t('auth.verifyExpired')}</span>
            ) : (
              <span>{t('auth.verifyExpiresIn')} {formatTime(secondsLeft)}</span>
            )}
          </div>

          <button type="submit" className="auth-button" disabled={loading || code.length !== 6 || expired}>
            {loading ? t('auth.verifyChecking') : t('auth.verifySubmit')}
          </button>
        </form>

        <div className="auth-footer">
          <button
            type="button"
            className="verify-resend-btn"
            onClick={handleResend}
            disabled={resendCooldown > 0}
          >
            {resendCooldown > 0
              ? `${t('auth.verifyResendWait')} (${resendCooldown}s)`
              : t('auth.verifyResend')}
          </button>
          <p>
            <Link to="/login">{t('auth.verifyBackToLogin')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
